/**
 * Bulk import — pick a folder of legacy single-study JSON files, idempotently
 * fold them into local + cloud storage. Re-runs are safe (same input → no-op).
 */

import { useRef, useState } from 'react';
import {
  bulkImportLegacyFiles,
  type BulkImportProgress,
  type BulkImportResult,
} from '@/state/persistence';

export function BulkImportSection() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<BulkImportProgress | null>(null);
  const [result, setResult] = useState<BulkImportResult | null>(null);
  const [isWorking, setWorking] = useState(false);
  const [overwriteDiff, setOverwriteDiff] = useState(true);
  const [showErrors, setShowErrors] = useState(false);

  const handlePick = () => inputRef.current?.click();

  const handleFiles = async (filesList: FileList | null) => {
    if (!filesList || filesList.length === 0) return;
    const files = Array.from(filesList).filter((f) => f.name.endsWith('.json'));
    if (files.length === 0) {
      setResult({ added: 0, updated: 0, skipped: 0, errors: [], totalGroupsAdded: 0 });
      return;
    }
    setWorking(true);
    setResult(null);
    setProgress({
      fileIndex: 0,
      totalFiles: files.length,
      fileName: '',
      added: 0,
      updated: 0,
      skipped: 0,
      errors: 0,
    });
    try {
      const out = await bulkImportLegacyFiles(files, {
        overwriteWhenContentDiffers: overwriteDiff,
        onProgress: (event) => setProgress(event),
      });
      setResult(out);
    } finally {
      setWorking(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <section className="border-t border-stone-200/50 pt-4 dark:border-stone-700/50">
      <h2 className="mb-1 text-lg font-semibold text-stone-800 dark:text-stone-200">
        Bulk Import Studies
      </h2>
      <p className="mb-3 text-xs text-stone-500 dark:text-stone-400">
        Pick a folder of legacy SKJV study JSON files. Imports are idempotent —
        identical content is skipped, drift is overwritten (toggle below), new
        studies are added with stable derived ids. Cloud sync picks up the
        result automatically.
      </p>

      <label className="mb-3 flex cursor-pointer items-center justify-between rounded-xl border border-stone-200 p-3 dark:border-stone-700">
        <div>
          <div className="text-sm font-medium text-stone-700 dark:text-stone-300">
            Overwrite local studies when source content differs
          </div>
          <div className="text-xs text-stone-500 dark:text-stone-400">
            Off keeps local edits untouched. On treats the source as authoritative.
          </div>
        </div>
        <button
          role="switch"
          aria-checked={overwriteDiff}
          onClick={() => setOverwriteDiff((v) => !v)}
          disabled={isWorking}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
            overwriteDiff ? 'bg-primary' : 'bg-stone-300 dark:bg-stone-600'
          }`}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
              overwriteDiff ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </button>
      </label>

      <input
        ref={inputRef}
        type="file"
        // @ts-expect-error — webkitdirectory + directory aren't in standard DOM types yet
        webkitdirectory=""
        directory=""
        multiple
        accept=".json,application/json"
        onChange={(e) => void handleFiles(e.target.files)}
        className="hidden"
      />

      <button
        onClick={handlePick}
        disabled={isWorking}
        className="rounded-lg bg-cyan-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-cyan-600 disabled:opacity-50"
      >
        {isWorking ? 'Importing…' : 'Pick folder of .json files'}
      </button>

      {progress && (
        <div className="mt-3 rounded-lg bg-stone-100 p-3 text-xs text-stone-600 dark:bg-stone-800 dark:text-stone-400">
          <div className="mb-1 font-mono">
            [{progress.fileIndex}/{progress.totalFiles}] {progress.fileName}
          </div>
          <div className="font-mono">
            added: {progress.added} · updated: {progress.updated} · skipped:{' '}
            {progress.skipped} · errors: {progress.errors}
          </div>
        </div>
      )}

      {result && (
        <div className="mt-3 rounded-lg bg-stone-100 p-3 text-xs text-stone-700 dark:bg-stone-800 dark:text-stone-300">
          <div className="mb-1 font-medium">Import complete.</div>
          <ul className="space-y-0.5">
            <li>Studies added: {result.added}</li>
            <li>Studies updated: {result.updated}</li>
            <li>Studies skipped (already current): {result.skipped}</li>
            <li>New groups added: {result.totalGroupsAdded}</li>
            <li>Errors: {result.errors.length}</li>
          </ul>
          {result.errors.length > 0 && (
            <button
              onClick={() => setShowErrors((v) => !v)}
              className="mt-2 underline"
            >
              {showErrors ? 'Hide errors' : 'Show errors'}
            </button>
          )}
          {showErrors && result.errors.length > 0 && (
            <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto rounded bg-stone-200/60 p-2 font-mono dark:bg-stone-700/60">
              {result.errors.map((e, i) => (
                <li key={i}>
                  <strong>{e.fileName}</strong>: {e.reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
